import { Combobox } from "@base-ui/react/combobox"
import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useMemo, useState } from "react"

const SearchableSelect = ({
  multiple = false,
  value,
  onValueChange,
  children,
  ...props
}) => (
  <Combobox.Root
    multiple={multiple}
    value={value}
    onValueChange={onValueChange}
    {...props}
  >
    {children}
  </Combobox.Root>
)

SearchableSelect.propTypes = {
  multiple: PropTypes.bool,
  value: PropTypes.any,
  onValueChange: PropTypes.func,
  children: PropTypes.node,
}

const Trigger = ({ className, variant = "button", children, ...props }) => (
  <Combobox.Trigger
    className={classNames(
      variant === "button" && "btn btn-secondary",
      className
    )}
    {...props}
  >
    <span className="block w-11/12 truncate">{children}</span>
    <Combobox.Icon
      className={classNames(
        "pointer-events-none flex shrink-0 items-center",
        variant === "button" ? "ml-2" : "pr-1"
      )}
    >
      <i
        className={classNames("text-gray-600", {
          "fa-kit fa-sf-chevron-down text-xxxs": variant === "button",
          "fa-kit fa-angle-down text-xs": variant !== "button",
        })}
      />
    </Combobox.Icon>
  </Combobox.Trigger>
)

Trigger.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf(["button", "input"]),
  children: PropTypes.node,
}

const Content = ({ className, children, ...props }) => (
  <Combobox.Portal>
    <Combobox.Positioner
      side="bottom"
      sideOffset={5}
      align="start"
      className="z-50"
    >
      <Combobox.Popup
        className={classNames(
          "min-w-[180px] overflow-hidden rounded bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none",
          className
        )}
        style={{ minWidth: "var(--anchor-width)" }}
        {...props}
      >
        {children}
      </Combobox.Popup>
    </Combobox.Positioner>
  </Combobox.Portal>
)

Content.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
}

const Input = ({ className, ...props }) => (
  <div className="border-b px-2 py-1.5">
    <Combobox.Input
      className={classNames(
        "w-full rounded border px-2 py-1.5 text-sm outline-none focus:border-blue-600",
        className
      )}
      autoComplete="off"
      {...props}
    />
  </div>
)

Input.propTypes = {
  className: PropTypes.string,
  placeholder: PropTypes.string,
}

const List = ({ className, children, ...props }) => (
  <Combobox.List
    className={classNames("max-h-60 overflow-auto p-1", className)}
    {...props}
  >
    {children}
  </Combobox.List>
)

List.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
}

const Item = ({ className, children, ...props }) => (
  <Combobox.Item
    className={classNames(
      "flex cursor-pointer items-center px-2 py-1.5 text-sm",
      "text-gray-800",
      "data-[highlighted]:rounded data-[highlighted]:bg-blue-800 data-[highlighted]:text-white",
      "data-[disabled]:cursor-not-allowed data-[disabled]:text-gray-400",
      className
    )}
    {...props}
  >
    <span className="mr-1.5 flex h-3.5 w-3.5 items-center justify-center">
      <Combobox.ItemIndicator>
        <i className="fa-solid fa-check text-xs" />
      </Combobox.ItemIndicator>
    </span>
    <span className="truncate">{children}</span>
  </Combobox.Item>
)

Item.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  value: PropTypes.any,
  disabled: PropTypes.bool,
}

const Empty = ({ className, children = "No results found", ...props }) => (
  <Combobox.Empty
    className={classNames(
      "px-2 py-4 text-center text-sm text-gray-500",
      className
    )}
    {...props}
  >
    {children}
  </Combobox.Empty>
)

Empty.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
}

const FilteredList = ({
  options,
  labelKey = "name",
  valueKey = "id",
  placeholder = "Search...",
  className,
  footer,
}) => {
  const [inputValue, setInputValue] = useState("")

  const filteredOptions = useMemo(() => {
    if (!inputValue) return options
    const query = inputValue.toLowerCase()
    return options.filter((option) =>
      (option[labelKey] || "").toLowerCase().includes(query)
    )
  }, [options, inputValue, labelKey])

  return (
    <Content className={className}>
      <Input
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
      />
      <List>
        {filteredOptions.map((option) => (
          <Item key={option[valueKey]} value={option[valueKey]}>
            {option[labelKey]}
          </Item>
        ))}
        {filteredOptions.length === 0 && <Empty />}
      </List>
      {footer}
    </Content>
  )
}

FilteredList.propTypes = {
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  labelKey: PropTypes.string,
  valueKey: PropTypes.string,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  footer: PropTypes.node,
}

SearchableSelect.Trigger = Trigger
SearchableSelect.Content = Content
SearchableSelect.Input = Input
SearchableSelect.List = List
SearchableSelect.Item = Item
SearchableSelect.Empty = Empty
SearchableSelect.FilteredList = FilteredList

export default SearchableSelect
